import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "react-toastify";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { abrirMidiaDaChamada } from "../../services/midiaChamada";
import { AuthContext } from "../Auth/AuthContext";
import usePermissions from "../../hooks/usePermissions";
import useModules from "../../hooks/useModules";

// Fase C4 — o estado da chamada, para o app inteiro.
//
// Mora acima do layout porque chamada entra em qualquer tela: quem está no
// Dashboard precisa ver o modal tanto quanto quem está no atendimento.
//
// Os eventos chegam DIRIGIDOS (`emitirParaUsuario` no backend, sala por
// usuário): nada aqui filtra destinatário, porque o servidor já mandou só
// para quem pode ver o número do cliente.

const ChamadaContext = createContext({});

export const useChamada = () => useContext(ChamadaContext);

// Erros da transferência (C5). Ficam aqui e não no i18n porque o texto do
// backend é um código: sem tradução a pessoa veria "ERR_CALL_TARGET_BUSY".
const ERROS_DA_TRANSFERENCIA = {
	ERR_CALL_NOT_OWNER: "Esta chamada não é mais sua.",
	ERR_CALL_TARGET_BUSY: "O atendente escolhido já está em outra chamada.",
	ERR_CALL_NO_AGENTS: "Nenhum atendente disponível neste departamento agora.",
	ERR_CALL_TRANSFER_TARGET: "Escolha um atendente ou um departamento.",
	ERR_CALL_TRANSFER_NOT_PENDING: "Não há transferência pendente."
};

// O erro do backend chega como código ou como mensagem do sidecar. Aqui
// interessa só ter ALGUM motivo para mostrar na barra — o texto completo já
// vai no toast.
const mensagemDeErro = (err) =>
	err?.response?.data?.error ||
	err?.response?.data?.message ||
	err?.message ||
	"motivo desconhecido";

const MOTIVO_DA_FALHA = {
	rejected: "A transferência foi recusada. A chamada continua com você.",
	timeout: "Ninguém atendeu a transferência. A chamada continua com você.",
	no_agents: "Nenhum atendente disponível. A chamada continua com você."
};

const daPayload = (dados) => ({
	callId: dados.callId,
	callLogId: dados.callLogId,
	whatsappId: dados.whatsappId,
	status: dados.status,
	direction: dados.direction,
	phoneNumber: dados.phoneNumber,
	contact: dados.contact || null,
	video: Boolean(dados.video),
	muted: Boolean(dados.muted),
	onHold: Boolean(dados.onHold),
	// Gravação em três campos, porque são três coisas diferentes: o que está
	// gravando de verdade, o que foi pedido, e por que o pedido não virou
	// gravação. Mostrar o pedido como se fosse gravação é o que faz o
	// atendente avisar o cliente de uma gravação que não existe.
	recording: Boolean(dados.recording),
	recordingRequested: Boolean(dados.recordingRequested),
	recordingError: dados.recordingError || null,
	// C5: quem passou a chamada adiante, quando esta é uma transferência.
	transferFrom: dados.transferFrom || null,
	// V0c: a IA de voz pediu um humano ({ voiceAgentName, motivo }).
	fromAi: dados.fromAi || null,
	timeoutSeconds: dados.timeoutSeconds || 0,
});

export const ChamadaProvider = ({ children }) => {
	const { user } = useContext(AuthContext);
	// Agente de voz é do módulo de ligações e de quem pode ligar: sem isso o
	// pedido só voltava 403 em toda tela aberta.
	const { temPermissao, carregando: carregandoPermissoes } = usePermissions();
	const { temModulo } = useModules();
	const comAgentesDeVoz = !carregandoPermissoes && temModulo("calls") && temPermissao("calls:use");

	// A que está entrando e ainda não tem dono (o modal de atender/recusar).
	const [entrando, setEntrando] = useState(null);
	// A que está no ar comigo (a tela de chamada ativa).
	const [ativa, setAtiva] = useState(null);
	const [estadoMidia, setEstadoMidia] = useState("");
	const [atendendo, setAtendendo] = useState(false);
	// C5: transferência que saiu daqui e ainda espera aceite do destino.
	const [transferenciaPendente, setTransferenciaPendente] = useState(null);
	// Agentes de voz ativos da empresa, para o menu "Atender com IA". Lista
	// vazia (ou erro de permissão) = o botão nem aparece.
	const [agentesDeVoz, setAgentesDeVoz] = useState([]);

	const midiaRef = useRef(null);
	const ativaRef = useRef(null);

	ativaRef.current = ativa;

	const fecharMidia = useCallback(() => {
		if (midiaRef.current) {
			midiaRef.current.fechar();
			midiaRef.current = null;
		}
		setEstadoMidia("");
	}, []);

	const encerrarLocal = useCallback(() => {
		fecharMidia();
		setAtiva(null);
		setEntrando(null);
		setAtendendo(false);
		setTransferenciaPendente(null);
	}, [fecharMidia]);

	// O áudio só abre depois do claim: o ticket de mídia exige ser o dono.
	// `aoConectar` roda uma vez, quando o WebSocket de mídia abre (o uplink já
	// é deste navegador): é o momento de tirar a IA da linha sem buraco.
	const abrirAudio = useCallback(
		async (callId, aoConectar) => {
			fecharMidia();

			let conectou = false;

			try {
				midiaRef.current = await abrirMidiaDaChamada({
					obterUrl: async () => {
						const { data } = await api.post(`/calls/${callId}/media-ticket`);
						return data?.wsUrl || "";
					},
					aoEstado: (estado) => {
						setEstadoMidia(estado);

						if (
							aoConectar &&
							!conectou &&
							(estado === "conectado" || estado === "aguardando_microfone")
						) {
							conectou = true;
							aoConectar();
						}
					},
				});
			} catch (err) {
				// Microfone negado não pode passar por "chamada muda": a pessoa
				// precisa saber que ninguém a está ouvindo.
				toast.error(
					"Não foi possível usar o microfone. Libere o acesso no navegador e atenda de novo."
				);
			}
		},
		[fecharMidia]
	);

	useEffect(() => {
		if (!user?.id || !comAgentesDeVoz) return;

		api
			.get("/calls/voice-agents")
			.then(({ data }) => setAgentesDeVoz(data || []))
			.catch(() => setAgentesDeVoz([]));
	}, [user?.id, comAgentesDeVoz]);

	useEffect(() => {
		if (!user?.id) return undefined;

		const socket = openSocket();

		socket.on("call.incoming", (dados) => {
			const chamada = daPayload(dados);

			// Chamada minha (eu disquei, ou já atendi) vai direto para a tela
			// ativa; a de ninguém abre o modal de atender. Transferência e
			// passagem da IA chegam com a ligação JÁ atendida (status
			// "completed") e também são oferta: sem isso o modal nunca abria.
			if (chamada.status === "ringing" || chamada.transferFrom || chamada.fromAi) {
				setEntrando(chamada);
			}
		});

		socket.on("call.status", (dados) => {
			const chamada = daPayload(dados);

			// Identidade não regride: evento sem `phoneNumber`/`contact` (os de
			// transferência mandam só o andamento) não pode apagar o que a
			// barra já mostra — era o que virava "número desconhecido" no meio
			// da chamada.
			const mesclar = (atual) => ({
				...atual,
				...chamada,
				phoneNumber: chamada.phoneNumber || atual.phoneNumber,
				contact: chamada.contact || atual.contact,
			});

			setAtiva((atual) =>
				atual && atual.callId === chamada.callId ? mesclar(atual) : atual
			);
			setEntrando((atual) =>
				atual && atual.callId === chamada.callId ? mesclar(atual) : atual
			);
		});

		socket.on("call.ended", (dados) => {
			if (ativaRef.current?.callId === dados.callId) {
				toast.info("Chamada encerrada.");
			}

			setEntrando((atual) =>
				atual && atual.callId === dados.callId ? null : atual
			);
			setAtiva((atual) => {
				if (!atual || atual.callId !== dados.callId) return atual;
				return null;
			});

			if (ativaRef.current?.callId === dados.callId) fecharMidia();

			setTransferenciaPendente((atual) =>
				atual && atual.callId === dados.callId ? null : atual
			);
		});

		// C5 — a transferência foi oferecida e espera aceite: a chamada
		// continua aqui, só ganha o aviso e o botão de desistir.
		socket.on("call.transfer.pending", (dados) => {
			setTransferenciaPendente({
				callId: dados.callId,
				toUserId: dados.toUserId || null,
				queueId: dados.queueId || null,
				timeoutSeconds: dados.timeoutSeconds || 0
			});
		});

		// Recusa ou tempo esgotado: a chamada nunca saiu daqui.
		socket.on("call.transfer.failed", (dados) => {
			setTransferenciaPendente((atual) =>
				atual && atual.callId === dados.callId ? null : atual
			);
			toast.info(
				MOTIVO_DA_FALHA[dados.reason] ||
					"A transferência não foi concluída. A chamada continua com você."
			);
		});

		// O destino aceitou: perdi a chamada, e com ela o microfone.
		socket.on("call.transferred", (dados) => {
			setTransferenciaPendente(null);

			if (ativaRef.current?.callId !== dados.callId) return;

			toast.info("Chamada transferida.");
			setAtiva(null);
			fecharMidia();
		});

		// Outro atendente levou. Fecha o modal aqui — sem isto sobra um botão
		// "Atender" que só devolve 409 ao ser clicado.
		socket.on("call.claimed", (dados) => {
			setEntrando((atual) =>
				atual && atual.callId === dados.callId ? null : atual
			);
		});

		return () => socket.disconnect();
	}, [user?.id, fecharMidia]);

	// Larga o microfone se a aba fechar no meio da chamada.
	useEffect(() => fecharMidia, [fecharMidia]);

	const atender = useCallback(async () => {
		if (!entrando || atendendo) return;

		setAtendendo(true);

		try {
			await api.post(`/calls/${entrando.callId}/answer`);

			const alvo = entrando;

			// A resposta do `/answer` é o CallLog cru (`externalCallId`, sem
			// `callId`): passá-la pelo `daPayload` zerava o `callId` da barra, e
			// dali em diante "Assumir", "Encerrar" e o `call.ended` não achavam
			// a chamada (`/calls/undefined/...` → ERR_CALL_NOT_FOUND).
			//
			// Com a IA ainda na linha, a barra mostra o agente e o "Assumir":
			// se o áudio daqui não abrir, é por ele que se tira a IA.
			setAtiva({
				...alvo,
				status: "completed",
				...(alvo.fromAi
					? { voiceAgent: { name: alvo.fromAi.voiceAgentName || "Agente de voz" } }
					: {}),
			});
			setEntrando(null);

			// Passagem da IA: a ponte só cai depois que o áudio daqui abriu —
			// antes disso o cliente ficaria sem ninguém na linha.
			await abrirAudio(
				alvo.callId,
				alvo.fromAi
					? () =>
							api
								.post(`/calls/${alvo.callId}/take-over`)
								.catch((err) => {
									// 409: a IA já tinha saído (desligou, ou a ponte caiu).
									if (err?.response?.status !== 409) throw err;
								})
								.then(() =>
									setAtiva((atual) =>
										atual?.callId === alvo.callId
											? { ...atual, voiceAgent: null }
											: atual
									)
								)
								.catch(toastError)
					: undefined
			);
		} catch (err) {
			// 409 é o claim perdido: informação, não falha do sistema.
			if (err?.response?.status === 409) {
				toast.info("Outro atendente já atendeu esta chamada.");
				setEntrando(null);
			} else {
				toastError(err);
			}
		}

		setAtendendo(false);
	}, [entrando, atendendo, abrirAudio]);

	// Atender com um agente de voz: a ponte de áudio sobe no servidor e o
	// microfone de quem clicou NÃO abre — o ticket de mídia é uso único, e um
	// segundo cliente nele roubaria o uplink da ponte (protocolo do caller).
	const atenderComIA = useCallback(
		async (voiceAgentId) => {
			if (!entrando || atendendo) return;

			const alvo = entrando;
			setAtendendo(true);

			try {
				const { data } = await api.post(`/calls/${alvo.callId}/answer-ai`, {
					voiceAgentId,
				});

				// `voiceAgent` é estado SÓ desta tela: fica fora do `daPayload`
				// porque o `call.status` que vem depois não carrega o campo, e a
				// mesclagem apagaria o nome do agente no meio da chamada.
				setAtiva({
					...alvo,
					status: "completed",
					voiceAgent:
						data?.voiceAgent ||
						agentesDeVoz.find((a) => a.id === voiceAgentId) ||
						null,
				});
				setEntrando(null);
			} catch (err) {
				if (err?.response?.status === 409) {
					toast.info("Outro atendente já atendeu esta chamada.");
					setEntrando(null);
				} else if (
					err?.response?.data?.error === "ERR_PLAN_LIMIT_VOICE_AI_MINUTES"
				) {
					// Recusado ANTES de atender: a chamada segue tocando e pode ser
					// atendida por gente.
					toast.info(
						"Os minutos de IA de voz do plano acabaram neste mês. A IA volta no dia 1, ou fale com o administrador para ampliar o plano."
					);
				} else if (err?.response?.status === 502) {
					// 502 é só a VAPI: a chamada JÁ foi atendida e o servidor não
					// desliga o cliente por causa disso. Ela é de quem clicou, e o
					// microfone precisa abrir — senão o cliente fala sozinho.
					toast.error(
						"O agente de voz não entrou na chamada. Ela é sua: fale pelo microfone."
					);
					setAtiva({ ...alvo, status: "completed" });
					setEntrando(null);
					await abrirAudio(alvo.callId);
				} else {
					// Chamada que não existe mais, sem permissão, sem crédito na
					// VAPI: nada foi atendido, e abrir o microfone aqui seria
					// prometer uma conversa que não existe.
					toastError(err);
					setEntrando(null);
				}
			}

			setAtendendo(false);
		},
		[entrando, atendendo, abrirAudio, agentesDeVoz]
	);

	// Tirar a IA da linha e assumir: a ligação continua, o microfone abre aqui.
	const assumirDaIA = useCallback(async () => {
		const alvo = ativaRef.current;

		if (!alvo) return;

		try {
			await api.post(`/calls/${alvo.callId}/take-over`);
			setAtiva((atual) => (atual ? { ...atual, voiceAgent: null } : atual));
			await abrirAudio(alvo.callId);
		} catch (err) {
			toastError(err);
		}
	}, [abrirAudio]);

	const recusar = useCallback(async () => {
		if (!entrando) return;

		const alvo = entrando;
		setEntrando(null);

		try {
			await api.post(`/calls/${alvo.callId}/reject`);
		} catch (err) {
			toastError(err);
		}
	}, [entrando]);

	const encerrar = useCallback(async () => {
		const alvo = ativaRef.current;

		if (!alvo) return;

		encerrarLocal();

		try {
			await api.post(`/calls/${alvo.callId}/end`);
		} catch (err) {
			toastError(err);
		}
	}, [encerrarLocal]);

	const silenciar = useCallback(async (valor) => {
		const alvo = ativaRef.current;

		if (!alvo) return;

		setAtiva((atual) => (atual ? { ...atual, muted: valor } : atual));
		// O sidecar é quem aplica o mudo; o comando vai pelo próprio socket de
		// mídia (reafirmado a cada reconexão) e pela API, que é o caminho que
		// vale quando o áudio ainda está reconectando.
		if (midiaRef.current) midiaRef.current.silenciar(valor);

		try {
			await api.post(`/calls/${alvo.callId}/mute`, { value: valor });
		} catch (err) {
			toastError(err);
		}
	}, []);

	const espera = useCallback(async (valor) => {
		const alvo = ativaRef.current;

		if (!alvo) return;

		setAtiva((atual) => (atual ? { ...atual, onHold: valor } : atual));

		try {
			await api.post(`/calls/${alvo.callId}/hold`, { value: valor });
		} catch (err) {
			toastError(err);
		}
	}, []);

	// Gravar a conversa, ligando e desligando no meio dela (LGPD).
	//
	// O estado da tela só muda depois do SIM do servidor: aqui, ao contrário
	// do mudo e da espera, mostrar otimista seria dizer "gravando" para uma
	// gravação que pode nem ter começado — e essa é a informação que a pessoa
	// repassa ao cliente.
	const gravar = useCallback(async (valor) => {
		const alvo = ativaRef.current;

		if (!alvo) return;

		try {
			const { data } = await api.post(`/calls/${alvo.callId}/recording`, {
				value: valor,
			});

			setAtiva((atual) =>
				atual
					? {
							...atual,
							recording: Boolean(data?.recording),
							recordingRequested: Boolean(data?.recordingRequested),
							// Deu certo: o motivo da falha anterior não vale mais.
							recordingError: null,
					  }
					: atual
			);

			// Pedido aceito pela política da empresa e gravação que não
			// aconteceu: quem está na conversa precisa saber AGORA, porque é
			// ela que avisa o cliente.
			if (valor && !data?.recording) {
				toast.error(
					"A gravação foi pedida mas não está acontecendo. Não avise o cliente de que a chamada está sendo gravada."
				);
			}
		} catch (err) {
			// O pedido falhou: o estado da tela não muda, e a barra continua
			// dizendo a verdade.
			setAtiva((atual) =>
				atual
					? { ...atual, recordingError: mensagemDeErro(err) }
					: atual
			);
			toastError(err);
		}
	}, []);

	// C5 — passar a chamada adiante. Com aceite (o padrão) a chamada CONTINUA
	// aqui até o destino aceitar; quem muda o estado é o socket, não a resposta.
	const transferir = useCallback(async ({ userId, queueId }) => {
		const alvo = ativaRef.current;

		if (!alvo) return false;

		try {
			const { data } = await api.post(`/calls/${alvo.callId}/transfer`, {
				userId: userId || undefined,
				queueId: queueId || undefined
			});

			if (data?.timeoutSeconds) {
				setTransferenciaPendente({
					callId: alvo.callId,
					toUserId: data.offeredToUserId || null,
					queueId: queueId || null,
					timeoutSeconds: data.timeoutSeconds
				});
			}

			return true;
		} catch (err) {
			const codigo = err?.response?.data?.message || err?.response?.data?.error;

			if (ERROS_DA_TRANSFERENCIA[codigo]) {
				toast.error(ERROS_DA_TRANSFERENCIA[codigo]);
			} else {
				toastError(err);
			}

			return false;
		}
	}, []);

	const cancelarTransferencia = useCallback(async () => {
		const pendente = transferenciaPendente;

		if (!pendente) return;

		setTransferenciaPendente(null);

		try {
			await api.post(`/calls/${pendente.callId}/transfer/cancel`);
		} catch (err) {
			toastError(err);
		}
	}, [transferenciaPendente]);

	const discar = useCallback(
		async (whatsappId, telefone, opcoes = {}) => {
			const { data } = await api.post("/calls", {
				whatsappId,
				phone: telefone,
				// Gravar desde o começo, quando quem ligou escolheu isso no
				// aviso. A palavra final é do modo da empresa, no servidor.
				record: Boolean(opcoes.gravar),
			});

			setAtiva({
				callId: data.externalCallId,
				callLogId: data.id,
				whatsappId: data.whatsappId,
				status: data.status,
				direction: "outgoing",
				// O número discado é a rede de segurança: veio vazio do servidor,
				// a barra ainda mostra para quem se está ligando — nunca "número
				// desconhecido" numa ligação que a própria pessoa digitou.
				phoneNumber: data.phoneNumber || String(telefone).replace(/\D/g, ""),
				// O backend resolve o contato do número na discagem (DiscarService):
				// com cadastro, a barra nasce mostrando o nome.
				contact: data.contact || null,
				video: false,
				muted: false,
				onHold: false,
				// Pedido, não realidade: a gravação só começa quando há mídia,
				// e quem confirma é o `call.status` do sidecar.
				recording: false,
				recordingRequested: Boolean(opcoes.gravar),
				recordingError: null,
			});

			await abrirAudio(data.externalCallId);

			return data;
		},
		[abrirAudio]
	);

	// Fase 5 do VAPI: a IA liga para o contato. O microfone de quem clicou NÃO
	// abre — o ticket de mídia é uso único, e um segundo cliente nele roubaria
	// o uplink da ponte (mesma regra do "Atender com IA").
	//
	// A ponte só sobe quando o cliente atende (o servidor cuida disso, pelo
	// webhook): aqui a barra já nasce mostrando o agente, com "Assumir" à mão.
	const discarComIA = useCallback(
		async (whatsappId, telefone, voiceAgentId, objetivo) => {
			const { data } = await api.post("/calls/dial-ai", {
				whatsappId,
				phone: telefone,
				voiceAgentId,
				objetivo: objetivo || "",
			});

			setAtiva({
				callId: data.externalCallId,
				callLogId: data.id,
				whatsappId: data.whatsappId,
				status: data.status,
				direction: "outgoing",
				phoneNumber: data.phoneNumber || String(telefone).replace(/\D/g, ""),
				contact: data.contact || null,
				video: false,
				muted: false,
				onHold: false,
				recording: false,
				recordingRequested: false,
				recordingError: null,
				voiceAgent:
					data?.voiceAgent ||
					agentesDeVoz.find((a) => a.id === Number(voiceAgentId)) ||
					null,
			});

			return data;
		},
		[agentesDeVoz]
	);

	const valor = useMemo(
		() => ({
			entrando,
			ativa,
			estadoMidia,
			atendendo,
			atender,
			atenderComIA,
			assumirDaIA,
			agentesDeVoz,
			recusar,
			encerrar,
			silenciar,
			espera,
			gravar,
			discar,
			discarComIA,
			transferenciaPendente,
			transferir,
			cancelarTransferencia,
		}),
		[
			entrando,
			ativa,
			estadoMidia,
			atendendo,
			atender,
			atenderComIA,
			assumirDaIA,
			agentesDeVoz,
			recusar,
			encerrar,
			silenciar,
			espera,
			gravar,
			discar,
			discarComIA,
			transferenciaPendente,
			transferir,
			cancelarTransferencia,
		]
	);

	return (
		<ChamadaContext.Provider value={valor}>{children}</ChamadaContext.Provider>
	);
};

export default ChamadaContext;
